/**
 * Form delivery.
 *
 * Posts to a Lambda behind CloudFront at /api/*. Same origin as the site, so
 * no CORS.
 *
 * The sha256 header is not optional. CloudFront's origin access control signs
 * requests to Lambda function URLs but does NOT sign the request body, so any
 * POST with a body fails signature validation with a 403
 * InvalidSignatureException. AWS's documented workaround is for the client to
 * compute the SHA-256 of the body and send it in x-amz-content-sha256.
 */

const MAILER_URL = '/api/mail'

async function sha256Hex(text) {
  const bytes = new TextEncoder().encode(text)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

async function send(payload) {
  const body = JSON.stringify(payload)

  const res = await fetch(MAILER_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-amz-content-sha256': await sha256Hex(body),
    },
    body,
  })

  const contentType = res.headers.get('content-type') ?? ''
  if (!contentType.includes('application/json')) {
    throw new Error('Online booking is temporarily unavailable.')
  }

  if (!res.ok) {
    let detail = 'That didn\u2019t send.'
    try {
      const data = await res.json()
      if (data?.detail) detail = data.detail
    } catch {
      /* keep the generic message */
    }
    throw new Error(detail)
  }

  return true
}

/** Rental request — arrives as "send this customer an invoice". */
export function sendBookingRequest(payload) {
  return send({ kind: 'booking', ...payload })
}

/** General contact message. */
export function sendContact(payload) {
  return send({ kind: 'contact', ...payload })
}
